import type {
  UpdateApprovalMutation,
  UpdateApprovalMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

import { mapUpdateApprovalVariables } from './approvalInputMapping';

/**
 * Maps the tRPC mutation response to match the GraphQL mutation structure.
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): UpdateApprovalMutation => ({
  update_approval_by_pk: {
    __typename: 'approval',
    Id: trpcData.Id,
  },
});

export const useUpdateApprovalTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.approval.update.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.approval.globalApprovals.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.approval.approvalLevels.queryKey(),
          }),
        ]);
      },
    }),
  });

  return {
    updateApproval: async (
      variables: UpdateApprovalMutationVariables
    ): Promise<UpdateApprovalMutation> => {
      const result = await mutation.mutateAsync(
        mapUpdateApprovalVariables(variables)
      );

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
